import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, CalendarCheck } from "lucide-react";
import { Label } from "../ui/label";
import { useLang } from "../../i18n/LanguageContext";
import { useJourney } from "../../context/JourneyContext";
import { CONTACT, BRAND } from "../../lib/landingData";
import { GuidePreview } from "./GuidePreview";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const LeadForm = () => {
  const { t } = useLang();
  const NEW = t.formNew;
  const { journey } = useJourney();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", location: "" });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [emailSent, setEmailSent] = useState(true);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.full_name.trim()) er.full_name = t.form.errName;
    if (!EMAIL_RE.test(form.email)) er.email = t.form.errEmail;
    if (form.phone.trim().length < 6) er.phone = t.form.errPhone;
    if (!consent) er.consent = NEW.errConsent;
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        ...form,
        consent,
        journey: journey || "foreign",
        site_url: process.env.REACT_APP_BACKEND_URL,
      }, { timeout: 45000 });
      setEmailSent(res.data?.guide_email_sent !== false);
      setDone(true);
    } catch (err) {
      toast.error(t.form.errorToast);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-cream/30 py-3 text-cream text-lg placeholder:text-cream/40 " +
    "focus:outline-none focus:border-cream transition-colors duration-300";

  return (
    <section id="lead-form" className="relative py-24 sm:py-32 bg-green-deep text-cream overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5">
          <span className="text-xs uppercase tracking-[0.25em] text-cream/70 font-semibold">{t.form.overline}</span>
          <h2 className="mt-5 font-display font-semibold tracking-tight leading-[1.03] text-3xl sm:text-4xl lg:text-5xl" data-testid="lead-form-title">
            {t.brand.form.title}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-cream/75 leading-relaxed max-w-md" data-testid="lead-form-sub">{t.brand.form.sub}</p>
          <GuidePreview />
        </div>

        <div className="lg:col-span-7">
          <p className="mb-4 text-sm text-cream/70" data-testid="form-tax-note">
            {t.brand.contactNote}{" "}
            <a href={BRAND.taxSite} target="_blank" rel="noopener noreferrer" data-testid="form-tax-link" className="underline underline-offset-2 text-cream hover:text-cream/80">
              {BRAND.taxSiteLabel}
            </a>
          </p>
          <div className="relative bg-green rounded-[4px] border border-cream/10 p-7 sm:p-10">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="thanks"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="py-6 text-center sm:text-left"
                  data-testid="lead-form-success"
                >
                  <CheckCircle2 className="h-12 w-12 text-cream/70 mx-auto sm:mx-0" strokeWidth={1.5} />
                  <h3 className="mt-5 font-display text-2xl sm:text-3xl text-cream">
                    {emailSent ? t.form.successTitle : NEW.fallbackTitle}
                  </h3>
                  {emailSent ? (
                    <p className="mt-3 text-cream/75 leading-relaxed max-w-md">
                      {t.form.successPre} <span className="text-cream font-medium">{form.email}</span> {t.form.successPost}
                    </p>
                  ) : (
                    <p className="mt-3 text-cream/75 leading-relaxed max-w-md" data-testid="lead-form-fallback">
                      {NEW.fallbackText}
                    </p>
                  )}
                  {!emailSent && (
                    <motion.a
                      href={`${API}/guide/download?journey=${journey || "foreign"}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="fallback-download"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                      className="mt-6 mr-3 inline-flex items-center gap-2 px-8 py-4 rounded-[3px] bg-cream text-green
                                 text-sm font-semibold uppercase tracking-wide transition-opacity hover:opacity-90"
                    >
                      {NEW.download}
                    </motion.a>
                  )}
                  <motion.a
                    href={CONTACT.booking}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="request-quote"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                    className={`mt-7 inline-flex items-center gap-2 px-8 py-4 rounded-[3px] text-sm font-semibold uppercase tracking-wide transition-opacity hover:opacity-90 ${emailSent ? "bg-cream text-green" : "border border-cream/40 text-cream"}`}
                  >
                    <CalendarCheck className="h-4 w-4" /> {NEW.quote}
                  </motion.a>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={submit}
                  noValidate
                  className="space-y-7"
                  data-testid="lead-form"
                >
                  <div>
                    <Label htmlFor="full_name" className="text-xs uppercase tracking-[0.2em] text-cream/60">
                      {t.form.name}
                    </Label>
                    <input id="full_name" type="text" data-testid="input-full-name" value={form.full_name}
                      onChange={update("full_name")} placeholder={t.form.namePh} className={inputCls} autoComplete="name" />
                    {errors.full_name && <p className="mt-2 text-sm text-cream/70" data-testid="error-full-name">{errors.full_name}</p>}
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-cream/60">
                      {t.form.email}
                    </Label>
                    <input id="email" type="email" data-testid="input-email" value={form.email}
                      onChange={update("email")} placeholder={t.form.emailPh} className={inputCls} autoComplete="email" />
                    {errors.email && <p className="mt-2 text-sm text-cream/70" data-testid="error-email">{errors.email}</p>}
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] text-cream/60">
                      {t.form.phone}
                    </Label>
                    <input id="phone" type="tel" data-testid="input-phone" value={form.phone}
                      onChange={update("phone")} placeholder={t.form.phonePh} className={inputCls} autoComplete="tel" />
                    {errors.phone && <p className="mt-2 text-sm text-cream/70" data-testid="error-phone">{errors.phone}</p>}
                  </div>

                  <div>
                    <Label htmlFor="location" className="text-xs uppercase tracking-[0.2em] text-cream/60">
                      {NEW.locationLabel}
                    </Label>
                    <input id="location" type="text" data-testid="input-location" value={form.location}
                      onChange={update("location")} placeholder={NEW.locationPh} className={inputCls} autoComplete="address-level2" />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-row">
                    <input
                      type="checkbox"
                      data-testid="input-consent"
                      checked={consent}
                      onChange={(e) => { setConsent(e.target.checked); setErrors((er) => ({ ...er, consent: undefined })); }}
                      className="mt-1 h-4 w-4 shrink-0 accent-white cursor-pointer"
                    />
                    <span className="text-sm text-cream/70 leading-relaxed">{NEW.consent}</span>
                  </label>
                  {errors.consent && <p className="-mt-3 text-sm text-cream/70" data-testid="error-consent">{errors.consent}</p>}

                  <motion.button
                    type="submit"
                    data-testid="lead-form-submit"
                    disabled={loading}
                    whileHover={{ scale: loading ? 1 : 1.02 }}
                    whileTap={{ scale: loading ? 1 : 0.98 }}
                    className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-[3px] bg-cream
                               text-green text-sm font-semibold uppercase tracking-wide transition-opacity
                               hover:opacity-90 disabled:opacity-70"
                  >
                    {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> {t.form.sending}</>) : t.brand.form.submit}
                  </motion.button>

                  <p className="text-center text-xs text-cream/50" data-testid="reassurance">{t.form.reassurance}</p>
                  <p className="text-center text-xs text-cream/40" data-testid="form-microcopy">{NEW.micro}</p>
                  <p className="text-center text-xs text-cream/40" data-testid="form-engagement">{t.brand.engagement}</p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
